import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import html2canvas from "html2canvas";
import { useNavigate } from "react-router-dom";
import { 
  Sparkles, Clock, CheckCircle2, CreditCard, Download, 
  Megaphone, Share2, ReceiptText, ShieldCheck ,Info
} from "lucide-react";

import {
  fetchMyCustomRequestsThunk,
  sendCustomizationRequestThunk,
} from "../../../features/eVisitingCard/VisitingCard.thunk";

import { clearCashPayment } from "../../../features/payment/paymentSlice";
import { createCashPayment, checkCashPaymentStatus } from "../../../features/payment/paymentThunks";
import PaymentOptionModal from "./pyment/PaymentOption";
import PaymentQRModal from "./pyment/PaymentQRModal";
import { setAdvertisingCard } from "../../../features/advertisement/advtCardSlice";
import useOnlinePayment from "../../../hooks/useOnlinePayment";
import PaymentSummaryModal from "./pyment/PymentSummary";

const IMAGE_BASE = process.env.REACT_APP_IMAGE_BASE_URL;

export default function CustomizedCardRequest() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isPolling, setIsPolling] = useState(false);
  const { customRequests, loading, cardData } = useSelector((state) => state.eCard);
 const requestsArray = Array.isArray(customRequests)
  ? customRequests
  : [];

const request = requestsArray.find(
  (r) =>
    r.status !== "cancelled" &&
    r.adminStatus !== "rejected" &&
    r.adminStatus !== "completed"
);


useEffect(() => {
  if (!request) return;
  if (request.adminStatus === "completed") return;

  const interval = setInterval(() => {
    setIsPolling(true);
    dispatch(fetchMyCustomRequestsThunk()).finally(() => {
      setIsPolling(false);
    });
  }, 5000);

  return () => clearInterval(interval);
}, [dispatch, request?.adminStatus]);


const requestStatus = Boolean(request);


  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentSuccessful, setPaymentSuccessful] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const { startOnlinePayment } = useOnlinePayment();
  const { cashPaymentData, status, payment_status } = useSelector((state) => state.payment);

  // MEMOIZED REFRESH FUNCTION (Avoids Page Reload)
  const refreshData = useCallback(() => {
    dispatch(fetchMyCustomRequestsThunk());
  }, [dispatch]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  useEffect(() => {
    if (!cashPaymentData?.orderId || paymentSuccessful) return;
    const interval = setInterval(() => {
      dispatch(checkCashPaymentStatus(cashPaymentData.orderId));
    }, 3000);
    return () => clearInterval(interval);
  }, [cashPaymentData?.orderId, paymentSuccessful, dispatch]);

  useEffect(() => {
    if (payment_status === "paid" && !paymentSuccessful) {
      setPaymentSuccessful(true);
      setTimeout(() => {
        setPaymentSuccessful(false);
        dispatch(clearCashPayment());
        refreshData(); // Updates state without reload
      }, 3500);
    }
  }, [payment_status, paymentSuccessful, refreshData, dispatch]);
// const latestRequest = customRequests?.[0];
  const customizedCard = cardData?.find((c) => c.name === "Customized Card");
  const customizedCardId = customizedCard?._id;

const uiState = !request
  ? "NO_REQUEST"
  : request.adminStatus === "pending"
  ? "PENDING_APPROVAL"
  : request.adminStatus === "approved"
  ? "PAYMENT_PENDING"
  : request.adminStatus === "paid"
  ? "PAID"
  : request.adminStatus === "completed"
  ? "COMPLETED"
  : "NO_REQUEST"; 


 const requestCustomization = async () => {
  if (!customizedCardId) return alert("Card ID missing");

  const res = await dispatch(sendCustomizationRequestThunk(customizedCardId));

  if (res.meta.requestStatus === "fulfilled") {
    dispatch(fetchMyCustomRequestsThunk()); // 🔥 force UI refresh
  }
};


  const handlePayNow = () => setSummaryOpen(true);
  const handleProceedToPay = () => { setSummaryOpen(false); setShowPaymentModal(true); };

  const handlePaymentSelect = async (method) => {
    if (!request?._id || !request?.totalAmount) return alert("Invalid payment data");
    setShowPaymentModal(false);
    if (method === "cash") {
      await dispatch(createCashPayment({
        bookingId: request._id,
        amount: request.totalAmount,
        paymentFor: "visiting-card",
      }));
    } else if (method === "online") {
      startOnlinePayment({
        bookingId: request._id,
        amount: request.totalAmount,
        paymentFor: "visiting-card",
        onSuccess: () => refreshData(),
        onFailure: (err) => console.error("Online payment failed:", err),
      });
    }
  };

  const downloadCard = async () => {
    const element = document.getElementById("customCardPreview");
    if (!element) return;
    const canvas = await html2canvas(element, { scale: 3, useCORS: true });
    const link = document.createElement("a");
    link.download = "customized-visiting-card.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const advertiseCard = async () => {
    const element = document.getElementById("customCardPreview");
    if (!element) return;
    const canvas = await html2canvas(element, { scale: 3, useCORS: true });
    dispatch(setAdvertisingCard({ cardType: "Customized Card", cardImage: canvas.toDataURL("image/png") }));
    navigate("/dashboard/card-advertising");
  };

  const shareCard = async () => {
    const element = document.getElementById("customCardPreview");
    if (!element) return;
    const canvas = await html2canvas(element, { scale: 2 });
    const base64 = canvas.toDataURL("image/png");
    if (!navigator.canShare) return alert("Sharing not supported");
    const blob = await (await fetch(base64)).blob();
    const file = new File([blob], "visiting-card.png", { type: blob.type });
    await navigator.share({ title: "My Visiting Card", files: [file] });
  };
if (loading && !isPolling) {
    <div className="d-flex justify-content-center align-items-center min-vh-50">
      <div className="spinner-grow text-primary" role="status"></div>
    </div>
};

  return (
    <div className="container-fluid py-4 animate-fade-in">
      <div className="row justify-content-center">
        <div className="col-12 col-md-10 col-lg-8 col-xl-6">
          
          <div className="mb-4 text-center">
            <h2 className="fw-bold text-dark mb-1">Customized Visiting Card</h2>
            <p className="text-muted small">Design your professional identity with ease</p>
          </div>

          {/* ================= NO REQUEST ================= */}
         {!requestStatus && (
  <div className="card border-0 shadow-sm glass-card text-center p-5">
    <div className="icon-badge bg-soft-primary mx-auto mb-4">
      <Sparkles size={40} className="text-primary" />
    </div>
    <h4 className="fw-bold">No Request Found</h4>
    <p className="text-muted px-md-5">
      Get a professional, customized e-visiting card designed specifically for your brand.
    </p>
    <button
      className="btn btn-primary btn-lg rounded-pill px-5 shadow mt-3"
      onClick={requestCustomization}
    >
     Start Customization
    </button>
  </div>
)}


          {/* ================= PENDING APPROVAL ================= */}
          {uiState === "PENDING_APPROVAL" && (
            <div className="card border-0 shadow-sm glass-card text-center p-5">
              <div className="icon-badge bg-soft-warning mx-auto mb-4 animate-pulse">
                <Clock size={40} className="text-warning" />
              </div>
              <h4 className="fw-bold">Review in Progress</h4>
              <p className="text-muted">Our admin is reviewing your customization request. This usually takes 24-48 hours.</p>
              <span className="badge bg-soft-warning text-warning py-2 px-3 mt-3">PENDING APPROVAL</span>
            </div>
          )}

          {/* ================= PAYMENT PENDING ================= */}
          {uiState === "PAYMENT_PENDING" && (
            <div className="card border-0 shadow-sm glass-card p-4">
              <div className="text-center mb-4">
                <div className="icon-badge bg-soft-success mx-auto mb-3">
                  <CheckCircle2 size={40} className="text-success" />
                </div>
                <h4 className="fw-bold">Request Approved!</h4>
                <p className="text-muted">Your card design is ready. Complete the payment to access it.</p>
              </div>

              <div className="bg-light rounded-4 p-4 text-center mb-4">
                <p className="text-secondary small mb-1">Total Payable</p>
                <h2 className="fw-black mb-0">₹{request.totalAmount || request.amount}</h2>
              </div>

              <button className="btn btn-success btn-lg w-100 rounded-3 py-3 shadow-sm d-flex align-items-center justify-content-center gap-2" onClick={handlePayNow}>
                <CreditCard size={20} /> Proceed to Pay
              </button>

              {cashPaymentData && (
                <div className="alert alert-warning mt-4 mb-0 border-0 rounded-3">
                   <small>Offline payment initiated. Awaiting manual verification.</small>
                </div>
              )}
            </div>
          )}

          {/* ================= PAYMENT RECEIVED (PAID) ================= */}
            {uiState === "PAID" && (
            <div className="card border-0 shadow-sm glass-card p-4 text-center animate-fade-in">
            <div className="bg-warning bg-opacity-10 p-3 rounded-3 d-flex align-items-center justify-content-center border border-warning border-opacity-25">
            <Info size={30} className="text-warning me-2" />
            <span className="small fw-medium">
             Your payment has been received. Our team is working on your custom card
             design, it will appear here after completion.
            </span>
           </div>
          <div className="mt-4">
          <span className="badge bg-warning text-dark px-3 py-2">
           DESIGN IN PROGRESS
          </span>
          </div>
         </div>
          )}

          {/* ================= COMPLETED ================= */}
          {uiState === "COMPLETED" && (
            <div className="animate-fade-in">
              <div className="card border-0 shadow-lg overflow-hidden rounded-4 mb-4">
                <div className="p-3 bg-white d-flex justify-content-between align-items-center border-bottom">
                  <div className="d-flex align-items-center gap-2">
                    <ShieldCheck className="text-success" size={20} />
                    <span className="fw-bold">Professional Card Ready</span>
                  </div>
                  <span className="badge bg-success">ACTIVE</span>
                </div>

                <div className="card-preview-area p-4 text-center bg-light">
                   <div id="customCardPreview" className="card-preview-container mx-auto shadow-sm overflow-hidden">
                      {request.cardImage ? (
                        <img src={`${IMAGE_BASE}${request.cardImage}`} alt="Custom Card" className="img-fluid" crossOrigin="anonymous" />
                      ) : (
                        <div className="p-5 text-muted small">Awaiting admin upload...</div>
                      )}
                   </div>
                </div>

                <div className="p-3 bg-white">
                  <div className="row g-2">
                    <div className="col-4">
                      <button className="btn btn-light w-100 py-2 small d-flex flex-column align-items-center gap-1" onClick={downloadCard}>
                        <Download size={18} /> Download
                      </button>
                    </div>
                    <div className="col-4">
                      <button className="btn btn-light w-100 py-2 small d-flex flex-column align-items-center gap-1" onClick={advertiseCard}>
                        <Megaphone size={18} /> Advertise
                      </button>
                    </div>
                    <div className="col-4">
                      <button className="btn btn-light w-100 py-2 small d-flex flex-column align-items-center gap-1" onClick={shareCard}>
                        <Share2 size={18} /> Share
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card border-0 shadow-sm glass-card p-3">
                <div className="d-flex align-items-center gap-2 mb-3 text-muted">
                  <ReceiptText size={18} />
                  <span className="fw-bold small">Transaction Details</span>
                </div>
                <div className="row g-3 small">
                  <div className="col-6 text-muted">Request ID</div>
                  <div className="col-6 text-end fw-medium">{request._id.slice(-8).toUpperCase()}</div>
                  <div className="col-6 text-muted">Date</div>
                  <div className="col-6 text-end fw-medium">{new Date(request.createdAt).toLocaleDateString()}</div>
                  <div className="col-6 text-muted">Amount Paid</div>
                  <div className="col-6 text-end fw-bold text-success">₹{request.amount}</div>
                </div>
              </div>
               <div className="bg-danger bg-opacity-10 p-3 mt-2 rounded-3 d-flex align-items-center justify-content-center border border-danger border-opacity-15">
             <Info size={30} className="text-info me-2" />
              <span className="small fw-medium">Requesting a new customized card will deactivate and delete your existing visiting card image. You will not be able to recover it.</span>
              </div>
              <div className="text-center">
              <button
      className="btn btn-primary btn-lg rounded-pill px-5 ms-4 shadow mt-3 align-items-center"
      onClick={requestCustomization}>
     Start Customization
    </button>
    </div>
            </div>
          )}
        </div>
      </div>

      <PaymentSummaryModal
        show={summaryOpen}
        slot={{ amount: request?.amount, gstPercentage: request?.gstPercentage || 0, gstAmount: request?.gstAmount || 0, totalAmount: request?.totalAmount }}
        onClose={() => setSummaryOpen(false)}
        onBookClick={handleProceedToPay}
      />
      <PaymentOptionModal open={showPaymentModal} onClose={() => setShowPaymentModal(false)} onSelect={handlePaymentSelect} />
      <PaymentQRModal
        open={Boolean(cashPaymentData)}
        data={cashPaymentData}
        status={status}
        payment_status={payment_status}
        onClose={() => { if (!paymentSuccessful) { dispatch(clearCashPayment()); setPaymentSuccessful(false); } }}
      />

      <style>{`
      .glass-card {
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0,0,0,0.05) !important;
  border-radius: 20px !important;
}

.bg-soft-primary { background-color: rgba(75, 106, 255, 0.1); }
.bg-soft-warning { background-color: rgba(255, 193, 7, 0.1); }
.bg-soft-success { background-color: rgba(40, 167, 69, 0.1); }

.icon-badge {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.fw-black { font-weight: 900; }

.card-preview-container {
  width: 280px;
  height: auto;
  min-height: 400px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #eee;
}

.animate-fade-in {
  animation: fadeIn 0.5s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.animate-pulse { animation: pulse 2s infinite; }
      `}</style>
    </div>
  );
}